import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { safeDebug, safeError } from '@/lib/safeLog';
import { persistSurveyAnswers } from '@/lib/persistSurveyAnswers';

/**
 * REK-1985 (SSO.4): fire-and-forget side effects that run once per real
 * sign-in (the Supabase provider's SIGNED_IN event; the Clerk provider's
 * null→active session transition). Extracted verbatim from
 * src/providers/AuthProvider.tsx so both providers share one implementation.
 *
 * All blocks are FIRE-AND-FORGET by design: on the Supabase side they are
 * invoked from inside onAuthStateChange, where awaiting RLS/edge calls blocks
 * the subscriber and freezes the app half-initialized (see the provider's
 * inline history). Keep them non-awaited.
 */

export interface PostSignInEffectsOptions {
  /** Legacy `profiles.id` UUID (identical under both auth providers). */
  userId: string;
  /**
   * Token semantics (three-state, preserving the pre-extraction behavior):
   * - `string` — Supabase provider: the event's access token, passed
   *   explicitly because the client's internal auth isn't applied yet at
   *   onAuthStateChange time.
   * - `null` — Supabase provider with NO token on the event: the
   *   token-requiring effects (unveil calls, referral) skip, exactly as the
   *   original inline blocks did; onboarding persistence still runs.
   * - `undefined` — Clerk provider: no explicit header; the client's
   *   `accessToken` closure injects a fresh Clerk token on every call (an
   *   explicit stale header would be worse).
   */
  accessToken?: string | null;
  queryClient: QueryClient;
}

function buildAuthHeaders(accessToken?: string | null): Record<string, string> | undefined {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
}

/**
 * REK-1727: Universal `unveil-braze identify` call. Merges the pre-signup
 * alias profile (`unveil_email:<lower(email)>`) into the post-signup profile
 * keyed by `external_id = userId`. Keyed off the AUTHENTICATED session email
 * (NOT any localStorage flag) so it works regardless of which device created
 * the alias and covers BOTH the OTP path (verifyOtp) AND the
 * magic-link-new-tab path (which bypasses verifyOtp entirely).
 *
 * Per-session guard via sessionStorage so we call /users/identify at most
 * once per browser session — running it on every sign-in tick would spam
 * Braze. The guard key is isolated and does NOT collide with the existing
 * fbLeadAttribution sessionStorage keys.
 */
export function runUnveilBrazeIdentify(accessToken?: string | null): void {
  if (accessToken === null) return; // Supabase event carried no token — skip (pre-extraction behavior)
  try {
    const guardKey = 'unveil_identify_done';
    const alreadyIdentified = sessionStorage.getItem(guardKey) === '1';
    if (alreadyIdentified) return;
    // Optimistic mark BEFORE invoke so a re-entrant sign-in burst doesn't
    // fire two identify calls. The Braze call is best-effort + idempotent
    // (merge_behavior: "merge"), so a network error followed by a retry on
    // the next session is acceptable; what we MUST avoid is dual in-flight
    // calls in the same session.
    sessionStorage.setItem(guardKey, '1');
    // The Supabase functions client returns `{ data, error }` on HTTP non-2xx
    // (e.g. 401 from missing JWT) instead of rejecting the promise — only
    // network-layer failures (FunctionsFetchError / FunctionsRelayError) hit
    // `.catch()`. Our edge function ALSO surfaces application-level failures
    // as `{ ok: false, error }` at HTTP 200 (best-effort no-op when Braze env
    // is missing). Both modes need to roll back the per-session guard so the
    // next sign-in can retry, otherwise a single failure would leave the
    // alias unmerged for the rest of the session (Cursor bugbot finding,
    // Medium severity).
    const rollbackGuard = (reason: string, detail: unknown) => {
      try { sessionStorage.removeItem(guardKey); } catch { /* ignore */ }
      safeError(`unveil-braze identify ${reason} (non-fatal):`, detail);
    };
    supabase.functions.invoke('unveil-braze', {
      body: { action: 'identify' },
      headers: buildAuthHeaders(accessToken),
    }).then((res) => {
      if (res.error) {
        rollbackGuard('returned error', res.error);
        return;
      }
      // Edge function returns `{ ok: boolean, ...}`. Treat ok:false as a
      // retry-worthy failure too.
      const data = res.data as { ok?: boolean; error?: unknown } | null;
      if (data && data.ok === false) {
        rollbackGuard('returned ok:false', data.error ?? null);
      }
    }).catch((err) => {
      rollbackGuard('invoke failed', err);
    });
  } catch (e) {
    safeError('Error scheduling unveil-braze identify:', e);
  }
}

/**
 * REK-1901: Persist the high-confidence demographics the Unveil scan surfaced
 * at signup into the user's profile (user_knowledge). The scan result is
 * display-only and is gone by the time the account exists (especially on the
 * magic-link-new-tab path), and storing that PII in localStorage is
 * disallowed — so the `unveil-profile-import` edge function RE-LOOKS-UP
 * Unveil using the verified session email and writes the mapped attributes
 * server-side. Fired at the universal sign-in choke point so it covers every
 * login path on any device.
 */
export function runUnveilProfileImport(accessToken?: string | null): void {
  if (accessToken === null) return; // Supabase event carried no token — skip (pre-extraction behavior)
  try {
    const importGuardKey = 'unveil_profile_import_done';
    const alreadyImported = sessionStorage.getItem(importGuardKey) === '1';
    if (alreadyImported) return;
    // Optimistic mark BEFORE invoke so a re-entrant sign-in burst doesn't
    // fire two imports; roll back on failure so a later session can retry.
    sessionStorage.setItem(importGuardKey, '1');
    const rollbackImportGuard = (reason: string, detail: unknown) => {
      try { sessionStorage.removeItem(importGuardKey); } catch { /* ignore */ }
      safeError(`unveil-profile-import ${reason} (non-fatal):`, detail);
    };
    supabase.functions.invoke('unveil-profile-import', {
      body: {},
      headers: buildAuthHeaders(accessToken),
    }).then((res) => {
      if (res.error) {
        rollbackImportGuard('returned error', res.error);
        return;
      }
      const data = res.data as { ok?: boolean; error?: unknown } | null;
      if (data && data.ok === false) {
        rollbackImportGuard('returned ok:false', data.error ?? null);
      }
    }).catch((err) => {
      rollbackImportGuard('invoke failed', err);
    });
  } catch (e) {
    safeError('Error scheduling unveil-profile-import:', e);
  }
}

/**
 * Process referral from localStorage for users who clicked a magic link.
 * This handles the case where ref= was in the URL during signup, stored in
 * localStorage, but the user completed auth in a new tab where the URL param
 * is gone. The process-referral edge function handles: checking if already
 * referred, creating the referral record, awarding points to BOTH referrer
 * and referred.
 */
export function runProcessReferral(options: PostSignInEffectsOptions): void {
  const { userId, accessToken, queryClient } = options;
  if (accessToken === null) return; // Supabase event carried no token — skip (pre-extraction behavior)
  try {
    const attributionStr = localStorage.getItem('reklaim_attribution');
    if (!attributionStr) return;
    const attribution = JSON.parse(attributionStr);
    const referralCode = attribution.ref;

    if (!referralCode) return;

    supabase.functions.invoke('process-referral', {
      body: { referralCode },
      headers: buildAuthHeaders(accessToken),
    }).then(({ data, error }) => {
      if (error) {
        safeError('Failed to process referral:', error);
        return;
      }

      if (data?.success) {
        safeDebug('Referral processed for user:', userId, 'Points awarded:', data.pointsAwarded);
        // Clear the ref from attribution to prevent duplicate processing
        delete attribution.ref;
        if (Object.keys(attribution).length > 0) {
          localStorage.setItem('reklaim_attribution', JSON.stringify(attribution));
        } else {
          localStorage.removeItem('reklaim_attribution');
        }
        // Invalidate wallet to show updated points
        queryClient.invalidateQueries({ queryKey: ['wallet'] });
      } else if (data?.alreadyReferred || data?.notEligible) {
        // User was already referred or is not a new account — clear the ref
        // so it isn't retried on every subsequent login
        safeDebug('Referral not applied (alreadyReferred or notEligible), clearing ref from attribution');
        delete attribution.ref;
        if (Object.keys(attribution).length > 0) {
          localStorage.setItem('reklaim_attribution', JSON.stringify(attribution));
        } else {
          localStorage.removeItem('reklaim_attribution');
        }
      } else {
        safeDebug('Referral processing returned:', data);
      }
    }).catch(err => {
      safeError('Error invoking process-referral:', err);
    });
  } catch (e) {
    safeError('Error processing referral from localStorage:', e);
  }
}

/**
 * Persist onboarding data (DOB, first/last name) for users who verified via
 * magic link in a new tab — the verifyOtp path in useFastAuth never runs for
 * those users, so localStorage is the only bridge. Falls back to splitting
 * `profiles.full_name` (set by the handle_new_user trigger from metadata)
 * when localStorage is empty (cross-browser magic link, incognito) so
 * campaign dedup works.
 */
export function runOnboardingDataPersistence(userId: string): void {
  (async () => {
    try {
      const onboardingFields = [
        { storageKey: 'reklaim_dob',       questionText: 'Date of birth' },
        { storageKey: 'reklaim_first_name', questionText: 'First name' },
        { storageKey: 'reklaim_last_name',  questionText: 'Last name' },
      ] as const;

      const fieldsWithValues = onboardingFields
        .map(f => ({ ...f, value: localStorage.getItem(f.storageKey) }))
        .filter((f): f is typeof f & { value: string } => f.value !== null);

      // If localStorage has values, use them (primary path: same-browser OTP)
      if (fieldsWithValues.length > 0) {
        const orFilter = fieldsWithValues
          .map(f => `question_text.ilike.${f.questionText}`)
          .join(',');

        const { data: questions } = await supabase
          .from('question_catalog')
          .select('id, question_text')
          .or(orFilter);

        if (questions) {
          // REK-1925: route survey_qm_* writes through the service-role
          // persist-survey-answers edge function (direct client writes to
          // those keys are blocked by a DB trigger — they gate campaign
          // payout completion).
          const answers = fieldsWithValues
            .map(f => {
              const question = questions.find(
                q => q.question_text.toLowerCase() === f.questionText.toLowerCase()
              );
              if (!question) return null;
              return {
                questionMappingId: question.id,
                value: f.value,
                source: 'onboarding' as const,
              };
            })
            .filter((a): a is NonNullable<typeof a> => a !== null);

          if (answers.length > 0) {
            // Best-effort: try every answer independently so a single failure
            // does not block the full_name write or localStorage cleanup
            // below (REK-1977 splits the batch into single writes since
            // identity fields are campaign-linked).
            for (const answer of answers) {
              const { error: ukError } = await persistSurveyAnswers([answer]);
              if (ukError) {
                safeError('Failed to persist onboarding survey answer:', ukError);
              }
            }
          }
        }

        const firstName = fieldsWithValues.find(f => f.storageKey === 'reklaim_first_name')?.value;
        const lastName  = fieldsWithValues.find(f => f.storageKey === 'reklaim_last_name')?.value;
        if (firstName || lastName) {
          const fullName = [firstName, lastName].filter(Boolean).join(' ');
          const { error: profileError } = await supabase.from('profiles').update({ full_name: fullName }).eq('id', userId);
          if (profileError) {
            safeError('Failed to update profiles.full_name from onboarding:', profileError);
            return;
          }
        }

        fieldsWithValues.forEach(f => localStorage.removeItem(f.storageKey));
      } else {
        // Fallback: localStorage empty (cross-browser magic link, incognito).
        // If profiles.full_name was set by the handle_new_user trigger from
        // metadata, create user_knowledge rows so campaign dedup works.
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', userId)
          .single();

        const fullName = profile?.full_name?.trim();
        if (fullName) {
          const nameParts = fullName.split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          const nameFields = [
            { questionText: 'First name', value: firstName },
            { questionText: 'Last name', value: lastName },
          ].filter(f => f.value);

          if (nameFields.length > 0) {
            const orFilter = nameFields.map(f => `question_text.ilike.${f.questionText}`).join(',');
            const { data: questions } = await supabase
              .from('question_catalog')
              .select('id, question_text')
              .or(orFilter);

            if (questions) {
              const { data: existingRows, error: existingKeysError } = await supabase
                .from('user_knowledge')
                .select('attribute_key')
                .eq('user_id', userId)
                .in('attribute_key', questions.map(q => `survey_qm_${q.id}`));

              if (existingKeysError) {
                safeError('Error loading existing user_knowledge keys for onboarding fallback:', existingKeysError);
                return;
              }

              const existingKeys = new Set(
                (existingRows || []).map(r => r.attribute_key)
              );

              // REK-1925: persist via the service-role edge function. The
              // insert-only-if-absent intent is preserved by pre-filtering
              // keys that already exist, so an existing answer is never
              // clobbered.
              const answers = nameFields
                .map(f => {
                  const question = questions.find(
                    q => q.question_text.toLowerCase() === f.questionText.toLowerCase()
                  );
                  if (!question) return null;
                  const key = `survey_qm_${question.id}`;
                  if (existingKeys.has(key)) return null;
                  return {
                    questionMappingId: question.id,
                    value: f.value,
                    source: 'onboarding' as const,
                  };
                })
                .filter((a): a is NonNullable<typeof a> => a !== null);

              if (answers.length > 0) {
                // Best-effort: continue even on individual failures
                // (REK-1977 splits batch into single writes).
                for (const answer of answers) {
                  const { error: ukFallbackError } = await persistSurveyAnswers([answer]);
                  if (ukFallbackError) {
                    safeError('Failed to persist onboarding fallback survey answer:', ukFallbackError);
                  }
                }
              }
            }
          }
        }
      }
    } catch (e) {
      safeError('Error persisting onboarding data from localStorage:', e);
    }
  })();
}

/**
 * Run the full post-sign-in side-effect set (Braze alias merge, Unveil
 * profile import, referral processing, onboarding data persistence). Called
 * once per real sign-in by whichever auth provider is active.
 */
export function runPostSignInEffects(options: PostSignInEffectsOptions): void {
  runUnveilBrazeIdentify(options.accessToken);
  runUnveilProfileImport(options.accessToken);
  runProcessReferral(options);
  runOnboardingDataPersistence(options.userId);
}
